import { action, computed, observable, reaction, makeObservable } from 'mobx';
import {ChangeEventHandler} from 'react';
import {SuccessResponse, uploadedFiles} from '../../../../../domain';
import {
  arraySwapItems,
  Disposers,
  HttpPromiseState,
  toastrHelper,
  toInt
} from '../../../../../lib/helpers';
import {SlideModel, SlideShowModel} from '../models';

let slideEditStoreIdSeq = 1;

export class SlideEditStore {
  readonly storeId: number;

  @observable
  imageUrl: string | null;
  @observable.ref
  imageFile: File | null;
  @observable.ref
  imageUpload: HttpPromiseState<SuccessResponse> | null;

  private readonly disposers = new Disposers();

  constructor(private readonly parent: SlideShowEditStore) {
    makeObservable(this);
    this.storeId = slideEditStoreIdSeq++;
    this.imageUrl = null;
    this.imageFile = null;
    this.imageUpload = null;
    this.setup();
  }

  @action
  handleImageFileChange: ChangeEventHandler<HTMLInputElement> =
    (event) => {
      const files = event.target.files;
      if (files === null || files.length === 0)
        this.imageFile = null;
      else
        this.imageFile = files[0];
    };

  @action
  uploadImage = () => {
    if (this.imageFile === null)
      return;
    if (this.imageUpload?.isPending === true)
      return;
    this.imageUpload = uploadedFiles.uploadImages(this.imageFile);
  };

  @action
  clearImage = () => {
    this.imageUrl = null;
  };

  @action
  sync = (model: SlideModel) => {
    this.imageUrl = model.imageUrl;
    this.imageFile = null;
    this.imageUpload = null;
  };

  @computed
  get valid(): boolean {
    return this.imageUrl !== null;
  }

  @computed
  get model(): SlideModel {
    return {
      imageUrl: this.imageUrl!
    };
  }

  private setup() {
    this.disposers.add(reaction(
      () => this.imageUpload?.state,
      this.afterImageUploaded));
  }

  @action
  private afterImageUploaded = () => {
    if (this.imageUpload?.isResolved !== true)
      return;
    const results = this.imageUpload.value.Results;
    if (results.Id) {
      // single image uploaded
      const tmp = this.imageUpload.value.Results.Id;
      const id = toInt(tmp);
      if (id === null)
        return;
      this.imageUrl = uploadedFiles.imageUrl(id);
      this.imageUpload = null;
      this.imageFile = null;
    }
    if (results.ImageIds) {
      const imageIds = JSON.parse(results.ImageIds) as number[];
      if (imageIds.length > 0)
        this.parent.addImages(this.storeId, imageIds);
    }
    if (results.Error !== null && results.Error !== undefined) {
      toastrHelper.error(results.Error);
    }
  };

  dispose = () => {
    this.disposers.dispose();
  };
}

export class SlideShowEditStore {
  @observable.shallow
  slides: SlideEditStore[];

  private readonly disposers = new Disposers();

  constructor() {
    makeObservable(this);
    this.slides = [];
    this.addSlide();
  }

  @action
  addSlide = () => {
    const slide = new SlideEditStore(this);
    this.slides.push(slide);
    this.disposers.add(slide);
  };

  addImages = (slideEditStoreId: number, imageIds: readonly number[]) => {
    let index = this.slides.findIndex(x => x.storeId === slideEditStoreId);
    if (index !== -1)
      this.removeSlide(index);
    if (index !== -1)
      index = this.slides.length;
    for (let i = 0; i < imageIds.length; i++) {
      let imageId = imageIds[i];
      let model: SlideModel = {imageUrl: uploadedFiles.imageUrl(imageId)};
      const slide = new SlideEditStore(this);
      slide.sync(model);
      this.slides.splice(index + i, 0, slide);
      this.disposers.add(slide);
    }
  }

  @action
  removeSlide = (index: number) => {
    const slide = this.slides[index];
    slide?.dispose();
    this.slides.splice(index, 1);
  };

  @action
  moveBefore(index: number) {
    arraySwapItems(this.slides, index, index - 1);
  }

  @action
  moveAfter(index: number) {
    arraySwapItems(this.slides, index, index + 1);
  }

  @action
  sync(model: SlideShowModel) {
    for (let slide of this.slides)
      slide.dispose();
    this.slides.length = 0;
    for (let i = 0; i < model.slides.length; i++) {
      let slide = model.slides[i];
      this.addSlide();
      const store = this.slides[i];
      store.sync(slide);
    }
  }

  @computed
  get valid() {
    if (this.slides.length < 1)
      return false;
    for (let slide of this.slides)
      if (!slide.valid)
        return false;
    return true;
  }

  @computed
  get x9Contents(): string {
    if (!this.valid)
      return '';
    const slides: SlideModel[] = [];
    for (let slide of this.slides)
      slides.push(slide.model);
    const model: SlideShowModel = {
      slides: slides
    };
    return JSON.stringify(model);
  }

  dispose() {
    this.disposers.dispose();
  }
}
